import type { ReactElement, ReactNode } from 'react'
import { isDefined, isNotDefined } from '@terros/common'
import { Group, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { SizePrimitives } from '@front/model'
import { ButtonCopy } from './buttons'

export type OptionalRowInPlaceEdit = {
  type: 'in-place'
  onEdit?: VoidFunction
  editor?: ReactNode
  disabled?: boolean
}

export type OptionalRowEditStep = {
  type: 'edit-step'
  onEdit: VoidFunction
  disabled?: boolean
}

export type OptionalRowEdit = OptionalRowInPlaceEdit | OptionalRowEditStep

export type OptionalRowItem = {
  label: string
  value: ReactNode
  tooltip?: string
  canCopy?: boolean
  copyValue?: string
  edit?: OptionalRowEdit
}

export const OptionalRow = ({ value, canCopy, copyValue, edit, ...rest }: OptionalRowItem): ReactNode => {
  if (isNotDefined(value) && typeof value !== 'number') return

  return (
    <Group grow p='sm' preventGrowOverflow={false} wrap='nowrap'>
      <Label {...rest} />
      <Group justify='end' mih={SizePrimitives['9']} miw={0} wrap='nowrap'>
        <RowValue canCopy={canCopy} value={value} copyValue={copyValue} edit={edit} />
      </Group>
    </Group>
  )
}

const RowValue = ({
  value,
  canCopy,
  copyValue,
  edit,
}: Pick<OptionalRowItem, 'value' | 'canCopy' | 'copyValue' | 'edit'>): ReactNode => {
  const rowValue = <EditableValue edit={edit} value={value} />

  if (canCopy && isDefined(value)) {
    const copyText = isDefined(copyValue) ? copyValue : value.toString()
    return (
      <Group gap='xs' justify='end'>
        {rowValue}
        <ButtonCopy value={copyText} />
      </Group>
    )
  }
  return rowValue
}

const EditableValue = ({ value, edit }: Pick<OptionalRowItem, 'value' | 'edit'>): ReactNode => {
  if (isNotDefined(edit)) return <DisplayValue value={value} />
  if (edit.type === 'in-place' && isDefined(edit.editor))
    return (
      <Group justify='end' miw={0}>
        {edit.editor}
      </Group>
    )
  if (edit.disabled || isNotDefined(edit.onEdit)) return <DisplayValue value={value} />

  return (
    <Group justify='end'>
      <UnstyledButton
        className='px-xl py-md hover:bg-neutral-light-hover focus-visible:bg-neutral-light-hover rounded-sm'
        type='button'
        onClick={edit.onEdit}
      >
        <DisplayValue value={value} />
      </UnstyledButton>
    </Group>
  )
}

const DisplayValue = ({ value }: Pick<OptionalRowItem, 'value'>): ReactNode => {
  if (['string', 'number', 'boolean'].includes(typeof value)) return <Text ta='right'>{stringifyValue(value)}</Text>
  return <Group justify='end'>{value}</Group>
}
const stringifyValue = (value: ReactNode): ReactNode => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return value
}

const Label = ({ label, tooltip }: Pick<OptionalRowItem, 'label' | 'tooltip'>): ReactElement => {
  if (tooltip) {
    return (
      <Tooltip label={tooltip}>
        <Text c='dimmed' fw={500}>
          {label}
        </Text>
      </Tooltip>
    )
  }
  return (
    <Text c='dimmed' fw={500}>
      {label}
    </Text>
  )
}
